import { jwtDecode } from 'jwt-decode';
import { ApiConfigration } from '../common-classes/api-configuration';
import { UserService } from './user.service';
import { dispatch } from '../store/store';
import { setToken, setUserData } from '../store/slices/auth.slice';

interface MagicLinkJwtPayload {
    user_id?: number;
    exp?: number;
}

export const MAGIC_LINK_PARAM_KEYS = ['type', 'jwt', 'email', 'redirect'] as const;
export const MAGIC_LINK_TYPE = 'magic_link';

export type MagicLinkOutcome =
    | { kind: 'not-applicable' }
    | { kind: 'success'; redirectTo?: string }
    | { kind: 'reset-password'; email: string }
    | {
          kind: 'failure';
          reason: 'malformed-jwt' | 'no-token-in-payload' | 'login-failed';
      };

export const parseV2Jwt = (param: string): { token: string } | null => {
    try {
        const decoded = decodeURIComponent(param);
        const fixed = decoded.replace(':+', ':').replace(/'/g, '"');
        const parsed = JSON.parse(fixed);
        if (parsed && typeof parsed.token === 'string' && parsed.token.length) {
            return { token: parsed.token };
        }
        return null;
    } catch {
        return null;
    }
};

const isSafeRedirect = (value: string | null): value is string => {
    if (!value) return false;
    if (!value.startsWith('/')) return false;
    if (value.startsWith('//')) return false;
    if (value.startsWith('/\\')) return false;
    return true;
};

export const handleMagicLink = async (
    searchParams: URLSearchParams,
    language: string
): Promise<MagicLinkOutcome> => {
    if (searchParams.get('type') !== MAGIC_LINK_TYPE) {
        return { kind: 'not-applicable' };
    }

    const jwtParam = searchParams.get('jwt');
    const emailParam = searchParams.get('email');
    const redirectParam = searchParams.get('redirect');
    const redirectTo = isSafeRedirect(redirectParam) ? redirectParam : undefined;

    if (jwtParam) {
        const parsed = parseV2Jwt(jwtParam);
        if (!parsed) return { kind: 'failure', reason: 'malformed-jwt' };
        if (!parsed.token) return { kind: 'failure', reason: 'no-token-in-payload' };

        let userId: number;
        try {
            const payload = jwtDecode<MagicLinkJwtPayload>(parsed.token);
            if (typeof payload.user_id !== 'number') {
                return { kind: 'failure', reason: 'no-token-in-payload' };
            }
            userId = payload.user_id;
        } catch {
            return { kind: 'failure', reason: 'malformed-jwt' };
        }

        try {
            dispatch(setToken(parsed.token));
            const apiConfig = await new ApiConfigration().getApiConfig(
                language,
                parsed.token
            );
            const userService = new UserService(apiConfig);
            const user = await userService.getUserById(userId);
            dispatch(setUserData(user));
            return { kind: 'success', redirectTo };
        } catch {
            return { kind: 'failure', reason: 'login-failed' };
        }
    }

    if (emailParam) {
        return { kind: 'reset-password', email: emailParam };
    }

    return { kind: 'failure', reason: 'malformed-jwt' };
};

export const stripMagicLinkParams = (search: string): string => {
    const params = new URLSearchParams(search);
    MAGIC_LINK_PARAM_KEYS.forEach((key) => params.delete(key));
    const remaining = params.toString();
    return remaining ? `?${remaining}` : '';
};
